import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MediaMatcher } from '@angular/cdk/layout';
import { ActivatedRoute } from '@angular/router';

import { Subject, interval, combineLatest, BehaviorSubject } from 'rxjs';
import { takeUntil, map, takeWhile } from 'rxjs/operators';

import * as moment from 'moment';
import Swal from 'sweetalert2';
import {
  ADMIN_CONSTANT,
  UserMenu,
  GlobalLogout,
  GlobalUserInfo,
} from '../app.model';
import { AppService } from '../app.service';
import { DashboardService } from './dashboard.service';
import { BridgeDialogComponent } from '../shared/components/bridge-dialog/bridge-dialog.component';
import { ConfirmDialogComponent } from '../shared/components/confirm-dialog/confirm-dialog.component';
import { IdleService } from '../core/services/idle.service';
import { ChangePasswordComponent } from '../shared/components/change-password/change-password.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  private mobileQueryListener: () => void;
  isDay: boolean;
  activeLink: string;
  showDialog: boolean;
  userMenu: UserMenu[];
  mobileQuery: MediaQueryList;
  globalUserInfo: GlobalUserInfo;
  showTimerDialog: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  destroy: Subject<any> = new Subject<any>();

  constructor(
    private dialog: MatDialog,
    private media: MediaMatcher,
    private service: AppService,
    private cd: ChangeDetectorRef,
    private route: ActivatedRoute,
    private dashService: DashboardService,
    private idleService: IdleService,
  ) { }

  ngOnInit(): void {
    this.isDay = moment().hours() >= 6 && moment().hours() <= 18;
    this.idleService.onIdle().subscribe(isIdle => {
      if (isIdle) {
        console.log('User is idle. Logging out...');
      }
    });
    // Show warning 30s before logout
    this.idleService.onWarning().subscribe(() => {
      this.showIdleWarning();
    });
    this.mobileQuery = this.media.matchMedia('(max-width: 959px)');
    this.mobileQueryListener = () => this.cd.detectChanges();
    try {
      this.mobileQuery.addEventListener('change', this.mobileQueryListener);
    } catch (e) {
      try {
        // tslint:disable-next-line: deprecation
        this.mobileQuery.addListener(() => this.mobileQueryListener());
      } catch (err) {
        console.dir(e, err);
      }
    }
    this.globalUserInfo = this.service.getToken(
      ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_GLOBAL_USER_INFO
    ) as GlobalUserInfo;
    this.getSideMenu();
    this.service.setTitle(`Dashboard | ${this.globalUserInfo.UsrName}`);
    this.subscribeUserRoleNScreen();
    this.startSessionTimer();
  }

  ngOnDestroy(): void {
    this.destroy.next(null);
    this.destroy.complete();
    this.mobileQuery.removeEventListener('change', this.mobileQueryListener);
  }

  private showIdleWarning(): void {
    Swal.fire({
      title: 'Are you still there?',
      text: 'You will be logged out in 30 seconds due to inactivity.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Stay Logged In',
      cancelButtonText: 'Logout Now',
      timer: 30000, // Auto-close after 30s
      timerProgressBar: true
    }).then(result => {
      if (result.isConfirmed) {
        console.log('User chose to stay logged in.');
      } else {
        console.log('User chose to log out.');
        this.onLogout(true);
      }
    });
  }

  checkIfDay(): boolean {
    moment().format('h');
    return false;
  }

  onProfileAction(event: string): void {
    switch (event) {
      case ADMIN_CONSTANT.USER_PROFILE_CHANGE_PWD:
        this.changePassword();
        break;
      case ADMIN_CONSTANT.USER_PROFILE_ACTION:
        this.onLogout();
        break;
      case ADMIN_CONSTANT.USER_PROFILE_SWITCH:
        this.switchProfile();
        break;
      default:
        break;
    }
  }

  onLogout(force: boolean = false): void {
    const { Email, MobileCode, MobileNumber } = this.globalUserInfo;
    const _PAYLOAD: GlobalLogout = { Email, MobileCode, MobileNumber };
    this.service
      .onGlobalLogout(_PAYLOAD)
      .pipe(takeUntil(this.destroy))
      .subscribe(
        (response) => {
          if (response.Header.Status === ADMIN_CONSTANT.SUCCESS_STATUS) {
            this.service.onLogout(force);
          } else if (response.Header.Status === ADMIN_CONSTANT.ERROR_STATUS) {
            this.service.showToast(response.Error.ErrorMessage);
          }
        },
        (err) => this.service.showToast(ADMIN_CONSTANT.SERVER_ERROR_MESSAGE)
      );
  }

  private changePassword(): void {
    this.dialog
      .open(ChangePasswordComponent, {
        width: '325px',
        maxWidth: '325px',
        hasBackdrop: true,
        disableClose: true,
        position: { top: '15%' },
        data: {
          orgMap: [
            this.service.getToken(
              ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_SELECTED_ORGANIZATION
            ),
          ],
          email: this.service.getToken(
            ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_GLOBAL_USER_INFO
          ).Email,
          forgotPassword: true,
          resetPassword: false,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy))
      .subscribe();
  }

  private switchProfile(): void {
    this.service
      .requestAPI(true, 'GlobalUsrMap', '', {}, this.service.userData)
      .pipe(takeUntil(this.destroy))
      .subscribe(
        (response) => {
          if (response.Header.Status === ADMIN_CONSTANT.SUCCESS_STATUS) {
            this.dialog
              .open(BridgeDialogComponent, {
                width: '325px',
                maxWidth: '325px',
                hasBackdrop: true,
                disableClose: false,
                position: { top: '15%' },
                data: {
                  email: null,
                  orgMap: response.Body.BusinessData.OrgMap,
                },
              })
              .afterClosed()
              .pipe(takeUntil(this.destroy))
              .subscribe((d) => {
                if (!!d && !!d.OrgCode) {
                  this.service.selectedOrganization = d;
                  this.service.setToken(
                    ADMIN_CONSTANT.ADMIN_TOKEN_ORG_ID,
                    this.service.selectedOrganization.OrgId
                  );
                  this.service.setToken(
                    ADMIN_CONSTANT.ADMIN_TOKEN_USER_ID,
                    this.service.selectedOrganization.UsrId
                  );
                  this.service.setToken(
                    ADMIN_CONSTANT.ADMIN_TOKEN_ORG_CODE,
                    this.service.selectedOrganization.OrgCode
                  );
                  this.service.setToken(
                    ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_SELECTED_ULINK,
                    this.service.selectedOrganization.UniversalAppLink
                  );
                  this.service.setToken(
                    ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_SUPER_USER_FLAG,
                    this.service.selectedOrganization.SuperAdminFlag
                  );
                  this.service.setToken(
                    ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_SELECTED_ORGANIZATION,
                    this.service.selectedOrganization
                  );

                  window.location.href = 'dashboard';
                }
              });
          } else if (response.Header.Status === ADMIN_CONSTANT.ERROR_STATUS) {
            this.service.showToast(response.Error.ErrorMessage);
          }
        },
        (err) => this.service.showToast(ADMIN_CONSTANT.SERVER_ERROR_MESSAGE)
      );
  }

  private subscribeMenuChange(): void {
    this.dashService.userMenu
      .pipe(takeUntil(this.destroy))
      .subscribe((menu) => {
        if (!!menu && !!menu.length) {
          this.userMenu = menu;
        } else {
          this.service.showToast(ADMIN_CONSTANT.SERVER_ERROR_MESSAGE);
        }
      });
  }

  getSideMenu(): void {
    this.route.data.pipe(map((data) => data.menu)).subscribe(
      (response) => {
        if (response.Header.Status === ADMIN_CONSTANT.SUCCESS_STATUS) {
          // this.userMenu = response.Body.BusinessData.UserMenu;
          // subscribe to menu change
          this.subscribeMenuChange();
        } else if (response.Header.Status === ADMIN_CONSTANT.ERROR_STATUS) {
          this.service.showToast(response.Error.ErrorMessage);
        }
      },
      (err) => this.service.showToast(ADMIN_CONSTANT.SERVER_ERROR_MESSAGE)
    );
  }

  private subscribeUserRoleNScreen(): void {
    combineLatest([this.dashService.userScreen, this.service.userScreenCode])
      .pipe(takeUntil(this.destroy))
      .subscribe(([screen$, code$]) => {
        if (!!screen$ && !!screen$.length && code$) {
          const _SCREEN = screen$.find((f) => f.ScreenCode === code$);
          if (!_SCREEN) {
            this.service.showToast(ADMIN_CONSTANT.NO_ACCESS);
            this.service.accessCode = 'NO';
          } else if (!!_SCREEN && !!_SCREEN.ReadOnlyFlag) {
            this.service.accessCode = 'RO';
          } else {
            this.service.accessCode = 'RW';
          }
        }
      });
  }

  private startSessionTimer(): void {
    this.subscribeToShowTimerDialog();
    if (!this.service.getToken(ADMIN_CONSTANT.ADMIN_TOKEN_TIMER)) {
      this.service.setToken(ADMIN_CONSTANT.ADMIN_TOKEN_TIMER, 3600000 * 2);
    }
    interval(1000)
      .pipe(takeUntil(this.destroy))
      .subscribe(() => {
        if (
          Number(this.service.getToken(ADMIN_CONSTANT.ADMIN_TOKEN_TIMER)) <
          181000
        ) {
          this.showDialog = true;
          this.showTimerDialog.next(
            Number(this.service.getToken(ADMIN_CONSTANT.ADMIN_TOKEN_TIMER))
          );
        }
        if (
          Number(this.service.getToken(ADMIN_CONSTANT.ADMIN_TOKEN_TIMER)) <= 0
        ) {
          this.service.deleteToken(ADMIN_CONSTANT.ADMIN_TOKEN_TIMER);
          this.service.onLogout(true);
        } else {
          this.service.setToken(
            ADMIN_CONSTANT.ADMIN_TOKEN_TIMER,
            Number(this.service.getToken(ADMIN_CONSTANT.ADMIN_TOKEN_TIMER)) -
            1000
          );
        }
      });
  }

  private subscribeToShowTimerDialog(): void {
    this.showTimerDialog
      .pipe(takeWhile(() => !this.showDialog, true))
      .subscribe((s) => {
        if (s) {
          this.dialog.open(ConfirmDialogComponent, {
            width: '325px',
            position: { top: '10%' },
            data: {
              timer: s,
              noCancel: true,
              header: 'Session Timeout',
              message: `You will be automatically logged out after`,
            },
          });
        }
      });
  }
}
